import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { db, storage } from '../config/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import DocumentTypeSelectorModal from '../modules/construction/DocumentTypeSelectorModal';
import { getPaymentLink, getStage1Price } from '../config/paymentLinks';

const STAGE_3_4_ENABLED = true; // Feature flag for Stage 3 & 4

const AdminPanel = () => {
  const { 
    projects, 
    setProjects, 
    showNotification, 
    currentUser,
    isAdminUser,
    typeNames,
    calculateStage2PaymentStructure,
    documentTypes,
    initializeStage34RevisionTracking
  } = useAppContext();

  // State management
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedProject, setSelectedProject] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showProjectDetails, setShowProjectDetails] = useState({});

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showUploadedForms, setShowUploadedForms] = useState({});
  const [showSupportingDocs, setShowSupportingDocs] = useState({});
  const [showRevisionDocs, setShowRevisionDocs] = useState({});
  const [showDocumentTypeSelector, setShowDocumentTypeSelector] = useState(false);
  const [documentSelectorProject, setDocumentSelectorProject] = useState(null);
  const [documentSelectorAction, setDocumentSelectorAction] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState('');

  // Stage 1 Invoice Modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoicePaymentLink, setInvoicePaymentLink] = useState('');
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceUploading, setInvoiceUploading] = useState(false);

  // Receipt Upload Modal states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptProject, setReceiptProject] = useState(null);

  // Form states
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    description: ''
  });
  
  const [messageForm, setMessageForm] = useState({
    message: '',
    type: 'architect',
    attachment: null
  });

  const [documentForm, setDocumentForm] = useState({
    files: [],
    description: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    amount: '',
    description: '',
    quoteFile: null
  });

  // File upload progress
  const [uploadProgress, setUploadProgress] = useState({});

  // Check admin access
  useEffect(() => {
    if (!isAdminUser()) {
      showNotification('Access Denied', 'Admin privileges required', 'error');
      return;
    }
  }, [currentUser, isAdminUser]);

  // Filter projects based on stage availability
  const filteredProjects = projects
    .filter((project, index, self) => 
      // Remove duplicates based on project ID first
      index === self.findIndex(p => p.id === project.id)
    )
    .filter(project => {
      // Include all stages when STAGE_3_4_ENABLED is true
      const isCorrectStage = true;
      const matchesSearch = project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
      const matchesStage = filterStage === 'all' || project.stage.toString() === filterStage;
      return isCorrectStage && matchesSearch && matchesStatus && matchesStage;
    });

  // Sort projects by most recent first
  const sortedProjects = filteredProjects.sort((a, b) => {
    const dateA = new Date(a.createdDate || a.createdAt || 0);
    const dateB = new Date(b.createdDate || b.createdAt || 0);
    return dateB - dateA;
  });

  // Get project statistics (All stages)
  const getProjectStats = () => {
    if (!projects || projects.length === 0) {
      return { 
        total: 0, 
        active: 0, 
        paymentPending: 0, 
        quotesPending: 0, 
        stage1: 0, 
        stage2: 0, 
        stage3: 0, 
        stage4: 0 
      };
    }
    
    const allProjects = projects;
    const total = allProjects.length;
    const active = allProjects.filter(p => p.status && !['completed', 'stage-1-completed', 'stage-2-completed', 'stage-3-completed', 'stage-4-completed'].includes(p.status)).length;
    const paymentPending = allProjects.filter(p => p.status && (p.status.includes('awaiting-payment') || p.status.includes('awaiting-balance-payment'))).length;
    const quotesPending = allProjects.filter(p => p.status === 'quote-in-progress').length;
    const stage1 = allProjects.filter(p => p.stage === 1).length;
    const stage2 = allProjects.filter(p => p.stage === 2).length;
    const stage3 = allProjects.filter(p => p.stage === 3).length;
    const stage4 = allProjects.filter(p => p.stage === 4).length;
    
    return { total, active, paymentPending, quotesPending, stage1, stage2, stage3, stage4 };
  };

  const stats = getProjectStats();

  // FIXED: Real file upload function with proper error logging
  const uploadFileToStorage = async (file, projectId, path) => {
    try {
      console.log('Uploading to path:', `projects/${projectId}/${path}/${file.name}`);
      console.log('Current user:', currentUser?.email);
      console.log('File details:', { name: file.name, size: file.size, type: file.type });
      
      const storageRef = ref(storage, `projects/${projectId}/${path}/${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Upload successful, download URL:', downloadURL);
      
      return {
        name: file.name,
        url: downloadURL,
        size: file.size,
        type: file.type,
        uploadDate: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      console.error('Upload error details:', {
        code: error.code,
        message: error.message,
        path: `projects/${projectId}/${path}/${file.name}`,
        user: currentUser?.email
      });
      throw error;
    }
  };

  // Handle complexity quote request for Stage 2
  const handleRequestComplexityQuote = async (project) => {
    try {
      const projectRef = doc(db, 'projects', project.id);
      
      const updatedCommunications = [
        ...(project.communications || []),
        {
          type: 'architect',
          sender: 'planAIdesign',
          message: 'We have identified that your project requires a custom quote due to site complexity. Our team will assess the requirements and provide a tailored quote within 2-3 business days.',
          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
        }
      ];

      await setDoc(projectRef, {
        status: 'quote-in-progress',
        requiresComplexityAssessment: true,
        quoteRequestedDate: new Date().toISOString(),
        communications: updatedCommunications
      }, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          return {
            ...p,
            status: 'quote-in-progress',
            requiresComplexityAssessment: true,
            quoteRequestedDate: new Date().toISOString(),
            communications: updatedCommunications
          };
        }
        return p;
      }));

      showNotification('Complexity Assessment Initiated', 'Project marked for complexity assessment', 'success');
    } catch (error) {
      console.error('Error requesting complexity quote:', error);
      showNotification('Error', 'Failed to initiate complexity assessment', 'error');
    }
  };

  // Handle payment request with validation
  const handlePaymentRequest = async () => {
    if (!selectedProject || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      showNotification('Invalid Input', 'Please enter a valid payment amount', 'error');
      return;
    }

    if (!paymentForm.description.trim()) {
      showNotification('Description Required', 'Please provide a payment description', 'warning');
      return;
    }

    try {
      const projectRef = doc(db, 'projects', selectedProject.id);
      
      const updatedCommunications = [
        ...selectedProject.communications,
        {
          type: "architect",
          sender: "planAIdesign",
          message: paymentForm.description.toLowerCase().includes('final') 
            ? `${paymentForm.description} of $${paymentForm.amount} is required to finalise your project and release final documents. Please make payment at your earliest convenience.`
            : `${paymentForm.description} of $${paymentForm.amount} is required to proceed. Please make payment at your earliest convenience.`,
          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
        }
      ];

      await setDoc(projectRef, {
        paymentRequired: true,
        paymentAmount: paymentForm.amount,
        paymentDescription: paymentForm.description,
        communications: updatedCommunications
      }, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === selectedProject.id) {
          return {
            ...p,
            paymentRequired: true,
            paymentAmount: paymentForm.amount,
            paymentDescription: paymentForm.description,
            communications: updatedCommunications
          };
        }
        return p;
      }));
      
      setPaymentForm({ amount: '', description: '' });
      setShowPaymentModal(false);
      showNotification('Payment Request Sent', 'Payment request has been sent to client', 'success');
    } catch (error) {
      console.error('Error sending payment request:', error);
      showNotification('Error', 'Failed to send payment request', 'error');
    }
  };

  // Handle Stage 1 Invoice - Send Payment Link
  const handleStage1Invoice = async (project, invoiceFileToUpload = null) => {
    if (!project) return;
    
    try {
      setInvoiceUploading(true);
      
      // Generate Payment Link using existing paymentLinks utility
      const paymentLinkWithRef = getPaymentLink(1, project.type, null, { 
        projectId: project.id,
        townhouseUnits: project.townhouseUnits
      });
      
      if (!paymentLinkWithRef) {
        showNotification('Error', 'Could not generate payment link for this project type', 'error');
        setInvoiceUploading(false);
        return;
      }
      
      // Get the price using existing utility
      const price = getStage1Price(project.type, project.townhouseUnits) || 
                    project.paymentStructure?.paymentAmounts?.[0] || 
                    199;
      
      // Upload invoice file if provided
      let invoiceDocument = null;
      if (invoiceFileToUpload) {
        try {
          const uploadedInvoice = await uploadFileToStorage(
            invoiceFileToUpload,
            project.id,
            'invoices'
          );
          invoiceDocument = {
            ...uploadedInvoice,
            type: 'invoice',
            title: 'Tax Invoice',
            invoiceDate: new Date().toISOString()
          };
        } catch (uploadError) {
          console.error('Error uploading invoice:', uploadError);
          showNotification('Warning', 'Invoice upload failed, but payment link will still be sent', 'warning');
        }
      }
      
      const projectRef = doc(db, 'projects', project.id);
      
      const invoiceMessage = invoiceFileToUpload 
        ? `Your Tax Invoice and Draft Site Investigation & Feasibility Report are ready. Full payment of $${price} is required to release the Final Report. If not received within 5 business days, your saved card will be charged automatically.`
        : `Draft Site Investigation & Feasibility Report is ready. Full payment of $${price} is required to release the Final Report. If not received within 5 business days, your saved card will be charged automatically.`;
      
      const updatedCommunications = [
        ...(project.communications || []),
        {
          type: "architect",
          sender: "planAIdesign",
          message: invoiceMessage,
          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
        }
      ];

      const updateData = {
        status: 'payment-due',
        paymentStatus: 'payment-due',
        invoiceSentAt: new Date().toISOString(),
        paymentLink: paymentLinkWithRef,
        communications: updatedCommunications
      };
      
      // Add invoice document if uploaded
      if (invoiceDocument) {
        updateData.invoice = invoiceDocument;
      }

      await setDoc(projectRef, updateData, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          return {
            ...p,
            ...updateData
          };
        }
        return p;
      }));
      
      // Show the payment link in modal for admin to copy/share
      setInvoicePaymentLink(paymentLinkWithRef);
      setSelectedProject({...project, paymentLink: paymentLinkWithRef, invoice: invoiceDocument});
      setInvoiceUploading(false);
      setInvoiceFile(null);
      
      showNotification('Payment Requested', 'Payment request has been sent to client', 'success');
    } catch (error) {
      console.error('Error sending Stage 1 invoice:', error);
      showNotification('Error', 'Failed to send invoice', 'error');
      setInvoiceUploading(false);
    }
  };
  
  // Open Invoice Modal (for sending new invoice or viewing existing link)
  const openInvoiceModal = (project) => {
    if (project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent' ||
        project.status === 'payment-due' || project.paymentStatus === 'payment-due') {
      // Already sent - just show the payment link
      setInvoicePaymentLink(project.paymentLink || getPaymentLink(1, project.type, null, { 
        projectId: project.id,
        townhouseUnits: project.townhouseUnits
      }));
      setSelectedProject(project);
      setShowInvoiceModal(true);
    } else {
      // New invoice - show upload form
      setSelectedProject(project);
      setInvoiceFile(null);
      setInvoicePaymentLink('');
      setShowInvoiceModal(true);
    }
  };

  // Handle Receipt Upload
  const handleReceiptUpload = async () => {
    if (!receiptProject || !receiptFile) {
      showNotification('Error', 'Please select a receipt file', 'error');
      return;
    }

    try {
      setReceiptUploading(true);

      // Upload receipt file to Firebase Storage
      const uploadedReceipt = await uploadFileToStorage(
        receiptFile,
        receiptProject.id,
        'receipts'
      );

      const receiptDocument = {
        ...uploadedReceipt,
        type: 'receipt',
        title: 'Payment Receipt',
        receiptDate: new Date().toISOString()
      };

      const projectRef = doc(db, 'projects', receiptProject.id);

      // Add communication message
      const updatedCommunications = [
        ...(receiptProject.communications || []),
        {
          type: "architect",
          sender: "planAIdesign",
          message: "Your payment receipt has been uploaded and is available for download.",
          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
        }
      ];

      await setDoc(projectRef, {
        receipt: receiptDocument,
        receiptUploadedAt: new Date().toISOString(),
        communications: updatedCommunications
      }, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === receiptProject.id) {
          return {
            ...p,
            receipt: receiptDocument,
            receiptUploadedAt: new Date().toISOString(),
            communications: updatedCommunications
          };
        }
        return p;
      }));

      setReceiptUploading(false);
      setReceiptFile(null);
      setShowReceiptModal(false);
      setReceiptProject(null);

      showNotification('Receipt Uploaded', 'Payment receipt has been uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading receipt:', error);
      showNotification('Error', 'Failed to upload receipt', 'error');
      setReceiptUploading(false);
    }
  };

  // Handle document type selection for Stage 3/4 uploads
  const handleDocumentTypeSelected = (selected) => {
    if (!documentSelectorProject) return;
    
    if (documentSelectorAction === 'upload') {
      setSelectedDocumentType(selected.value);
      setSelectedProject(documentSelectorProject);
      setShowDocumentModal(true);
    }
    setShowDocumentTypeSelector(false);
    setDocumentSelectorProject(null);
    setDocumentSelectorAction('');
  };

  // Handle Stage 2 custom quote upload with real files
  const handleQuoteUpload = async () => {
    if (!selectedProject || !quoteForm.amount || !quoteForm.description) {
      showNotification('Missing Information', 'Please fill in all quote details', 'error');
      return;
    }

    try {
      let quoteFiles = [];
      
      if (quoteForm.quoteFile) {
        setUploadProgress(prev => ({ ...prev, quote: 0 }));
        const uploadedFile = await uploadFileToStorage(
          quoteForm.quoteFile, 
          selectedProject.id, 
          'quotes'
        );
        quoteFiles.push(uploadedFile);
        setUploadProgress(prev => ({ ...prev, quote: 100 }));
      }

      const amount = parseFloat(quoteForm.amount);
      const paymentStructure = {
        totalPayments: amount >= 500 ? 3 : 2,
        paymentAmounts: amount >= 500 ? 
          [150, (amount - 150) / 2, (amount - 150) / 2] :
          [150, amount - 150],
        paymentDescriptions: amount >= 500 ?
          ['Initial payment to commence work', 'Progress payment', 'Final payment to release documents'] :
          ['Initial payment to commence work', 'Final payment to release documents']
      };
// Initialize revision tracking for Stage 3 & 4 projects
let revisionTracking = null;
if (selectedProject.stage >= 3) {
  revisionTracking = initializeStage34RevisionTracking(selectedProject.type, {
    stage: selectedProject.stage,
    architecturalRevisions: 2,
    architecturalRevisionFee: 150,
    structuralRevisions: 1,
    structuralRevisionFee: 0,
  });
}
      const projectRef = doc(db, 'projects', selectedProject.id);
      
      const updatedProject = {
        status: 'quote-ready',
        cost: `$${quoteForm.amount}`,
        customQuoteAmount: amount,
        paymentStructure: paymentStructure,
        ...(revisionTracking && { documentRevisionTracking: revisionTracking }),
        deliverables: [
          ...(selectedProject.deliverables || []),
          {
            type: "quote-document",
            title: `Stage 2 Custom Quote`,
            description: quoteForm.description,
            files: quoteFiles.length > 0 ? quoteFiles : [`Stage_2_Custom_Quote.pdf`],
            uploadDate: new Date().toISOString().split('T')[0],
            version: 0,
            isRevision: false,
            isFinal: false
          }
        ],
        communications: [
          ...selectedProject.communications,
          {
            type: "architect",
            sender: "planAIdesign",
            message: `Your custom quote of $${quoteForm.amount} is ready for review. ${quoteForm.description}`,
            timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
          }
        ]
      };

      await setDoc(projectRef, updatedProject, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === selectedProject.id) {
          return { ...p, ...updatedProject };
        }
        return p;
      }));

      setQuoteForm({ amount: '', description: '', quoteFile: null });
      setShowQuoteModal(false);
      setUploadProgress({});
      showNotification("Quote Uploaded", "Stage 2 custom quote has been uploaded successfully", "success");
    } catch (error) {
      console.error('Error uploading quote:', error);
      showNotification('Error', 'Failed to upload quote', 'error');
      setUploadProgress({});
    }
  };

  // Download uploaded form (for admin)
  const downloadUploadedForm = async (file) => {
    try {
      if (file.url && file.url !== '#') {
        const link = document.createElement('a');
        link.href = file.url;
        link.download = file.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const content = `This is the uploaded form: ${file.name}\n\nUploaded on: ${file.uploadDate}\n\nIn a real application, this would be the actual uploaded document from the client.`;
        const blob = new Blob([content], { type: file.type || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      
      showNotification("Download Started", `Downloading client upload: ${file.name}`, "success");
    } catch (error) {
      console.error('Error downloading file:', error);
      showNotification('Error', 'Failed to download file', 'error');
    }
  };

  // Download supporting document
  const downloadSupportingDocument = async (file, projectName) => {
    try {
      if (file.url && file.url !== '#') {
        const link = document.createElement('a');
        link.href = file.url;
        link.download = file.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showNotification("Download Started", `Downloading: ${file.name}`, "success");
      } else {
        showNotification('Error', `No download URL available for ${file.name}`, 'error');
      }
    } catch (error) {
      console.error('Error downloading supporting document:', error);
      showNotification('Error', 'Failed to download file', 'error');
    }
  };

  // COMPLETELY FIXED: Handle document upload with proper Stage 3/4 support
  const handleDocumentUpload = async () => {
    // Verify authentication
    if (!currentUser) {
      showNotification('Authentication Required', 'Please log in to upload documents', 'error');
      return;
    }

    if (!selectedProject) {
      showNotification('No Project Selected', 'Please select a project first', 'error');
      return;
    }

    console.log('Admin uploading documents for:', selectedProject.name);

    // Handle Stage 3 & 4 projects
    if (selectedProject.stage >= 3) {
      if (!selectedDocumentType) {
        showNotification('Document Type Required', 'Please select a document type first', 'error');
        return;
      }
      
      // Handle quote uploads for Stage 3/4
      if (selectedDocumentType === 'quote') {
        setShowDocumentModal(false);
        setShowQuoteModal(true);
        return;
      }
      
      // Handle regular Stage 3/4 document uploads
      try {
        console.log('=== ADMIN DOCUMENT UPLOAD DEBUG ===');
        console.log('Project:', selectedProject);
        console.log('Selected document type:', selectedDocumentType);
        
        const docTypes = selectedProject.stage === 3 ? documentTypes?.stage3 : documentTypes?.stage4;
        console.log('Available doc types for stage:', docTypes);
        
        const documentTypeLabel = docTypes?.[selectedDocumentType] || selectedDocumentType;
        console.log('Document type label:', documentTypeLabel);
        
        // Check if this is a revision
        const docRevisionTracking = selectedProject.documentRevisionTracking?.[selectedDocumentType];
        const isRevision = docRevisionTracking && docRevisionTracking.documents && docRevisionTracking.documents.length > 0;
        const revisionNumber = isRevision ? docRevisionTracking.documents.length : 0;
        
        console.log('Is revision:', isRevision, 'Revision number:', revisionNumber);
        
        // FIXED: Handle file uploads for Stage 3/4 with proper path and error handling
        let uploadedFiles = [];
        
        if (documentForm.files.length > 0) {
          console.log('Uploading', documentForm.files.length, 'files to Firebase Storage for Stage 3/4...');
          
          try {
            for (let i = 0; i < documentForm.files.length; i++) {
              const file = documentForm.files[i];
              
              setUploadProgress(prev => ({ 
                ...prev, 
                [file.name]: Math.round(((i + 1) / documentForm.files.length) * 100) 
              }));
              
              // FIXED: Use proper path for Stage 3/4 uploads
              const uploadedFile = await uploadFileToStorage(
                file, 
                selectedProject.id, 
                `documents/stage${selectedProject.stage}/${selectedDocumentType}`
              );
              
              uploadedFiles.push(uploadedFile);
              console.log('Uploaded Stage 3/4 file:', file.name, 'to path:', `documents/stage${selectedProject.stage}/${selectedDocumentType}`);
            }
          } catch (uploadError) {
            console.error('Error uploading files for Stage 3/4:', uploadError);
            console.error('Upload error details:', {
              code: uploadError.code,
              message: uploadError.message,
              path: `projects/${selectedProject.id}/documents/stage${selectedProject.stage}/${selectedDocumentType}`,
              user: currentUser?.email,
              projectStage: selectedProject.stage,
              documentType: selectedDocumentType
            });
            setUploadProgress({});
            showNotification('Upload Error', `Failed to upload files: ${uploadError.message}`, 'error');
            return;
          }
        } else {
          // Use placeholder files if no real files selected
          uploadedFiles = [
            {
              name: `${selectedDocumentType}_documents.pdf`,
              url: "#",
              uploadDate: new Date().toISOString()
            },
            {
              name: `${selectedDocumentType}_drawings.pdf`,
              url: "#", 
              uploadDate: new Date().toISOString()
            }
          ];
        }
        
        const deliverable = {
  type: "project-documents",
  documentType: selectedDocumentType || '',
  title: `${documentTypeLabel} ${isRevision ? `- Revision ${revisionNumber}` : ''}`,
  description: documentForm.description || `${documentTypeLabel} documentation`,
  files: uploadedFiles || [],
  uploadDate: new Date().toISOString().split('T')[0],
  version: revisionNumber || 0,
  isRevision: Boolean(isRevision),
  isFinal: false
};
        
        console.log('Document deliverable created:', deliverable);
        
        let message;
        if (selectedDocumentType === 'consultant-agreements') {
          message = `Consultant Agreements uploaded for you to sign and return.`;
        } else if (selectedDocumentType === 'application-package') {
          message = `Application Package is ready for review. Once approved, we will proceed with lodgement.`;
        } else if (selectedDocumentType === 'construction-documentation-package') {
          message = `Construction Documentation Package is ready. This completes the Stage 4 documentation.`;
        } else {
          message = `${documentTypeLabel} has been uploaded for your review. Please download and review the files.`;
        }
        
        console.log('Message to be sent:', message);
        
        // Update document revision tracking FIRST
        console.log('Updating revision tracking...');
        const updatedTracking = { ...(selectedProject.documentRevisionTracking || {}) };
        if (!updatedTracking[selectedDocumentType]) {
          updatedTracking[selectedDocumentType] = {
            revisionsUsed: 0,
            maxIncluded: 1,
            documents: []
          };
        }
        updatedTracking[selectedDocumentType].documents.push({
  uploadDate: new Date().toISOString(),
  version: revisionNumber || 0
});

        const projectRef = doc(db, 'projects', selectedProject.id);
        
        const updatedProject = {
  documentRevisionTracking: updatedTracking,
  deliverables: [...(selectedProject.deliverables || []), deliverable],
  documentsReady: true,
  communications: [
    ...(selectedProject.communications || []),
    {
      type: "architect",
      sender: "planAIdesign",
      message: message,
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
    }
  ],
  lastUpdated: new Date().toISOString()
};
        
        console.log('Attempting Firebase save...');
        const cleanedProject = JSON.parse(JSON.stringify(updatedProject));
console.log('Cleaned project for Firebase:', cleanedProject);
await setDoc(projectRef, cleanedProject, { merge: true });
        console.log('Firebase save successful');
        
        // Update local state
        setProjects(prev => prev.map(p => {
          if (p.id === selectedProject.id) {
            return updatedProject;
          }
          return p;
        }));

        // Clear form and close modal
        setDocumentForm({ files: [], description: '' });
        setUploadProgress({});
        showNotification("Documents Uploaded", "Documents uploaded successfully!", "success");
        setSelectedDocumentType('');
        setShowDocumentModal(false);
        console.log('=== END ADMIN DOCUMENT UPLOAD ===');
        
      } catch (error) {
        console.error('Error uploading Stage 3/4 documents:', error);
        setUploadProgress({});
        showNotification('Upload Error', `Failed to upload documents: ${error.message}`, 'error');
      }
      return;
    }

    // Existing Stage 1 & 2 logic remains unchanged
    if (selectedProject.stage === 2 && selectedProject.status === 'quote-in-progress') {
      setShowDocumentModal(false);
      setSelectedProject(selectedProject);
      setShowQuoteModal(true);
      return;
    }

    // Stage 1 & 2 upload logic (unchanged)
    try {
      let uploadedFiles = [];
      let deliverable;
      let message;
      let newStatus = selectedProject.status;

      if (documentForm.files.length > 0) {
        console.log('Uploading', documentForm.files.length, 'files to Firebase Storage...');
        
        for (let i = 0; i < documentForm.files.length; i++) {
          const file = documentForm.files[i];
          
          setUploadProgress(prev => ({ 
            ...prev, 
            [file.name]: Math.round(((i + 1) / documentForm.files.length) * 100) 
          }));
          
          const uploadedFile = await uploadFileToStorage(
            file, 
            selectedProject.id, 
            'documents'
          );
          
          uploadedFiles.push(uploadedFile);
          console.log('Uploaded file:', file.name);
        }
      }

      if (uploadedFiles.length === 0) {
        if (selectedProject.stage === 1) {
          uploadedFiles = [
            {name: "Feasibility_Report.pdf", url: "#", uploadDate: new Date().toISOString()}
          ];
        } else {
          uploadedFiles = [
            {name: "Concept_Design.pdf", url: "#", uploadDate: new Date().toISOString()}
          ];
        }
      }

      if (selectedProject.stage === 1) {
        deliverable = {
          type: "feasibility-report",
          title: "Site Investigation & Feasibility Report",
          description: documentForm.description || "Complete feasibility assessment",
          files: uploadedFiles,
          uploadDate: new Date().toISOString(),
          version: 0,
          isRevision: false,
          isFinal: true
        };
        message = "";
        newStatus = "documentation-ready";
      } else if (selectedProject.stage === 2) {
        const revisionRound = selectedProject.revisions?.currentRound || 0;
        const paymentsMade = selectedProject.paymentsMade || 0;
        const totalPayments = selectedProject.paymentStructure?.payments || 2;
        const allPaymentsComplete = paymentsMade >= totalPayments;
        
        deliverable = {
          type: "concept-design",
          title: allPaymentsComplete ? 'Final Concept Design Documentation' : (revisionRound === 0 ? 'Initial Concept Design' : `Revision ${revisionRound} Design`),
          description: documentForm.description || "Concept design package",
          files: uploadedFiles,
          uploadDate: new Date().toISOString(),
          version: revisionRound,
          isRevision: revisionRound > 0,
          isFinal: allPaymentsComplete
        };
        
        if (allPaymentsComplete) {
          message = "Final documentation uploaded. Thank you for choosing planAIdesign!";
          newStatus = "stage-2-completed";
        } else {
          message = `${deliverable.title} has been uploaded and is ready for review.`;
          newStatus = revisionRound > 0 ? `revision-${revisionRound}-complete` : "initial-concept-ready";
        }
      }

      const projectRef = doc(db, 'projects', selectedProject.id);
      
      const updatedProject = {
        status: newStatus,
        deliverables: [...(selectedProject.deliverables || []), deliverable],
        documentsReady: true,
        communications: [
          ...(selectedProject.communications || []),
          {
            type: "architect",
            sender: "planAIdesign",
            message: message,
            timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
          }
        ],
        lastUpdated: new Date().toISOString()
      };

      const cleanedProject = JSON.parse(JSON.stringify(updatedProject));
      await setDoc(projectRef, updatedProject, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === selectedProject.id) {
          return { ...p, ...updatedProject };
        }
        return p;
      }));

      setDocumentForm({ files: [], description: '' });
      setShowDocumentModal(false);
      setUploadProgress({});
      showNotification("Documents Uploaded", "Documents uploaded successfully!", "success");
      
    } catch (error) {
      console.error('Error in handleDocumentUpload:', error);
      showNotification('Upload Error', error.message || 'Failed to upload documents', 'error');
      setUploadProgress({});
    }
  };

  // Handle sending message with optional file attachment
  const handleSendMessage = async () => {
    if (!selectedProject || !messageForm.message.trim()) {
      showNotification('Message Required', 'Please enter a message', 'warning');
      return;
    }

    try {
      let attachment = null;
      
      if (messageForm.attachment) {
        setUploadProgress(prev => ({ ...prev, attachment: 0 }));
        attachment = await uploadFileToStorage(
          messageForm.attachment, 
          selectedProject.id, 
          'messages'
        );
        setUploadProgress(prev => ({ ...prev, attachment: 100 }));
      }

      const projectRef = doc(db, 'projects', selectedProject.id);
      
      const newMessage = {
        type: messageForm.type,
        sender: "planAIdesign",
        message: messageForm.message,
        timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
        ...(attachment && { attachment })
      };

      const updatedCommunications = [
        ...selectedProject.communications,
        newMessage
      ];

      await setDoc(projectRef, {
        communications: updatedCommunications
      }, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === selectedProject.id) {
          return {
            ...p,
            communications: updatedCommunications
          };
        }
        return p;
      }));

      setMessageForm({ message: '', type: 'architect', attachment: null });
      setShowMessageModal(false);
      setUploadProgress({});
      showNotification("Message Sent", "Message sent to client successfully", "success");
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Error', 'Failed to send message', 'error');
      setUploadProgress({});
    }
  };

  // Handle status update
  const handleStatusUpdate = async (project, newStatus) => {
    try {
      const projectRef = doc(db, 'projects', project.id);
      
      await setDoc(projectRef, {
        status: newStatus
      }, { merge: true });

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          return { ...p, status: newStatus };
        }
        return p;
      }));

      showNotification("Status Updated", `Status changed to: ${newStatus}`, "success");
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('Error', 'Failed to update status', 'error');
    }
  };

  // Helper functions
  const canShowComplexityQuote = (project) => {
    return project.stage === 2 && 
           project.canRequestComplexityQuote && 
           project.status !== 'quote-in-progress' && 
           project.status !== 'quote-ready' &&
           project.status !== 'quote-approved' &&
           project.status !== 'quote-rejected';
  };

  const getUniqueStatuses = () => {
    const stage12Projects = projects.filter(p => p.stage <= 2);
    const statuses = [...new Set(stage12Projects.map(p => p.status))];
    return statuses.sort();
  };

  const toggleProjectDetails = (projectId) => {
    setShowProjectDetails(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleUploadedForms = (projectId) => {
    setShowUploadedForms(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleSupportingDocs = (projectId) => {
    setShowSupportingDocs(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleRevisionDocs = (projectId) => {
    setShowRevisionDocs(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  if (!isAdminUser()) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">Admin privileges required to access this panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold">
              <span className="text-lg">plan</span>
              <span className="text-yellow-400 text-2xl">AI</span>
              <span className="text-lg">design</span>
              <span className="ml-2 text-sm bg-purple-800 px-2 py-1 rounded">Admin Panel - All Stages</span>
            </h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-100">Admin: {currentUser.name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('projects')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'projects'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Project Management
            </button>
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
                <div className="text-sm text-gray-600">Total Projects</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                <div className="text-3xl font-bold text-blue-600">{stats.active}</div>
                <div className="text-sm text-gray-600">Active Projects</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                <div className="text-3xl font-bold text-red-600">{stats.paymentPending}</div>
                <div className="text-sm text-gray-600">Payment Pending</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                <div className="text-3xl font-bold text-yellow-600">{stats.quotesPending}</div>
                <div className="text-sm text-gray-600">Custom Quotes Pending</div>
              </div>
            </div>

            {/* Stage Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Stage 1 Projects</h3>
                <div className="text-3xl font-bold text-green-600">{stats.stage1}</div>
                <div className="text-sm text-gray-600">Site Investigation & Feasibility</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Stage 2 Projects</h3>
                <div className="text-3xl font-bold text-blue-600">{stats.stage2}</div>
                <div className="text-sm text-gray-600">Concept Design</div>
              </div>
              {STAGE_3_4_ENABLED && (
                <>
                  <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                    <h3 className="text-lg font-semibold mb-4">Stage 3 Projects</h3>
                    <div className="text-3xl font-bold text-indigo-600">{stats.stage3}</div>
                    <div className="text-sm text-gray-600">Authority Approval Documentation</div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                    <h3 className="text-lg font-semibold mb-4">Stage 4 Projects</h3>
                    <div className="text-3xl font-bold text-purple-600">{stats.stage4}</div>
                    <div className="text-sm text-gray-600">Construction Documentation</div>
                  </div>
                </>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Recent Projects</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {sortedProjects.slice(0, 5).map(project => (
                    <div key={project.id} className="flex items-center justify-between p-4 bg-gray-50 rounded">
                      <div>
                        <h4 className="font-medium">{project.name}</h4>
                        <p className="text-sm text-gray-600">
                          {project.client} • Stage {project.stage} • {project.cost}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        project.status.includes('completed') ? 'bg-green-100 text-green-800' :
                        project.status.includes('awaiting-payment') ? 'bg-red-100 text-red-800' :
                        project.status.includes('progress') || project.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status.replace(/-/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* Filters and Search */}
            <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Search Projects</label>
                  <input
                    type="text"
                    placeholder="Search by name, client, address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Filter by Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  >
                    <option value="all">All Statuses</option>
                    {getUniqueStatuses().map(status => (
                      <option key={status} value={status}>
                        {status.replace(/-/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Filter by Stage</label>
                  <select
                    value={filterStage}
                    onChange={(e) => setFilterStage(e.target.value)}
                    className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  >
                    <option value="all">All Stages</option>
                    <option value="1">Stage 1</option>
                    <option value="2">Stage 2</option>
                    <option value="3">Stage 3</option>
                    <option value="4">Stage 4</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <div className="text-sm text-gray-600">
                    Showing {sortedProjects.length} projects
                  </div>
                </div>
              </div>
            </div>

            {/* Projects List */}
            <div className="space-y-4">
              {sortedProjects.map(project => (
                <div key={project.id} className="bg-white rounded-lg shadow-lg border border-gray-200">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">{project.name}</h3>
                        <p className="text-sm text-gray-600">
                          {project.client} ({project.email}) • {typeNames[project.type]} • Stage {project.stage} • {project.cost}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Created: {new Date(project.createdDate || project.createdAt).toLocaleDateString()} • 
                          Address: {project.address}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          project.status.includes('completed') ? 'bg-green-100 text-green-800' :
                          project.status.includes('approved') ? 'bg-green-100 text-green-800' :
                          project.status === 'quote-rejected' ? 'bg-red-100 text-red-800' :
                          project.status.includes('awaiting-payment') || project.status.includes('awaiting-balance-payment') ? 'bg-red-100 text-red-800' :
                          project.status.includes('progress') || project.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                          project.status.includes('revision') && project.status.includes('complete') ? 'bg-green-100 text-green-800' :
                          project.status.includes('revision') && project.status.includes('requested') ? 'bg-orange-100 text-orange-800' :
                          project.status.includes('ready') ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status.replace(/-/g, ' ')}
                        </span>
                        <button
                          onClick={() => toggleProjectDetails(project.id)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          {showProjectDetails[project.id] ? 'Hide Details' : 'Show Details'}
                        </button>
                      </div>
                    </div>

                    {/* Admin Actions */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {canShowComplexityQuote(project) && (
                        <button
                          onClick={() => handleRequestComplexityQuote(project)}
                          className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                        >
                          Request Complexity Quote
                        </button>
                      )}

                      {project.stage === 2 && project.status === 'quote-in-progress' && (
                        <button
                          onClick={() => {
                            setSelectedProject(project);
                            setShowQuoteModal(true);
                          }}
                          className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                        >
                          Upload Custom Quote
                        </button>
                      )}

                      {/* Stage 1 with captured card: Show Request Payment or View Payment Link */}
                      {project.stage === 1 && project.paymentMethodId && 
                       project.paymentStatus !== 'paid' && project.status !== 'paid' ? (
                        <button
                          onClick={() => openInvoiceModal(project)}
                          className={`${
                            project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent' ||
                            project.status === 'payment-due' || project.paymentStatus === 'payment-due'
                              ? 'bg-blue-600 hover:bg-blue-700'
                              : 'bg-green-600 hover:bg-green-700'
                          } text-white px-3 py-1 rounded text-sm`}
                        >
                          {project.status === 'invoice-sent' || project.paymentStatus === 'invoice-sent' ||
                           project.status === 'payment-due' || project.paymentStatus === 'payment-due'
                            ? '🔗 View Payment Link'
                            : '📧 Request Payment'}
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedProject(project);
                            setShowPaymentModal(true);
                          }}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                        >
                          Request Payment
                        </button>
                      )}

                      {/* Upload Receipt button - shows for paid Stage 1 projects */}
                      {project.stage === 1 && (project.paymentStatus === 'paid' || project.status === 'paid') && (
                        <button
                          onClick={() => {
                            setReceiptProject(project);
                            setReceiptFile(null);
                            setShowReceiptModal(true);
                          }}
                          className={`${project.receipt ? 'bg-gray-500' : 'bg-teal-600 hover:bg-teal-700'} text-white px-3 py-1 rounded text-sm`}
                        >
                          {project.receipt ? '📄 Receipt Uploaded ✓' : '📄 Upload Receipt'}
                        </button>
                      )}

                      <button
                        onClick={() => {
                          if (project.stage >= 3) {
                            setDocumentSelectorProject(project);
                            setDocumentSelectorAction('upload');
                            setShowDocumentTypeSelector(true);
                          } else {
                            setSelectedProject(project);
                            setShowDocumentModal(true);
                          }
                        }}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Upload Documents
                      </button>

                      <button
                        onClick={() => {
                          setSelectedProject(project);
                          setShowMessageModal(true);
                        }}
                        className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                      >
                        Send Message
                      </button>

                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleStatusUpdate(project, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                      >
                        <option value="">Update Status</option>
                        <option value="awaiting-payment">Awaiting Payment</option>
                        <option value="awaiting-client-feedback">Awaiting Client Feedback</option>
                        <option value="in-progress">In Progress</option>
                        <option value="payment-due">Payment Due</option>
                        <option value="quote-ready">Quote Ready</option>
                        <option value="paid">Paid</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>

                    {/* Project Details - Collapsed by default */}
                    {showProjectDetails[project.id] && (
                      <div className="border-t pt-4 space-y-4">
                        {/* Project Description */}
                        <div className="bg-gray-50 p-4 rounded">
                          <h4 className="font-semibold text-sm mb-2">Project Description</h4>
                          <p className="text-sm text-gray-700">{project.description}</p>
                          {project.brief && (
                            <div className="mt-2">
                              <h5 className="font-medium text-xs mb-1">Detailed Requirements:</h5>
                              <p className="text-xs text-gray-600 whitespace-pre-wrap">{project.brief}</p>
                            </div>
                          )}
                        </div>

                        {/* Supporting Documents */}
                        {project.supportingDocuments && project.supportingDocuments.length > 0 && (
                          <div className="bg-orange-50 p-4 rounded">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="font-semibold text-sm">Supporting Documents</h4>
                              <button
                                onClick={() => toggleSupportingDocs(project.id)}
                                className="text-orange-600 hover:text-orange-800 text-sm"
                              >
                                {showSupportingDocs[project.id] ? 'Hide' : 'Show'} ({project.supportingDocuments.length})
                              </button>
                            </div>
                            
                            {showSupportingDocs[project.id] && (
                              <div className="space-y-2">
                                {project.supportingDocuments.map((file, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-white p-2 rounded">
                                    <div>
                                      <p className="text-xs font-medium">{file.name}</p>
                                      <p className="text-xs text-gray-600">
                                        {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB • ` : ''}
                                        {file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : 'Uploaded with project'}
                                      </p>
                                      {!file.url && (
                                        <p className="text-xs text-red-500">No download URL available</p>
                                      )}
                                    </div>
                                    {file.url && (
                                      <button
                                        onClick={() => downloadSupportingDocument(file, project.name)}
                                        className="bg-orange-600 text-white px-2 py-1 rounded text-xs hover:bg-orange-700"
                                      >
                                        Download
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Payment History */}
                        {project.paymentHistory && project.paymentHistory.length > 0 && (
                          <div className="bg-green-50 p-4 rounded">
                            <h4 className="font-semibold text-sm mb-2">Payment History</h4>
                            <div className="space-y-2">
                              {project.paymentHistory.map((payment, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span>Payment {payment.paymentNumber}: {payment.description || 'Payment'}</span>
                                  <span className="font-medium">${payment.amount} - {new Date(payment.date).toLocaleDateString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Client Uploaded Forms */}
                        {project.uploadedForms && project.uploadedForms.length > 0 && (
                          <div className="bg-teal-50 p-4 rounded">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="font-semibold text-sm">Client Uploaded Forms</h4>
                              <button
                                onClick={() => toggleUploadedForms(project.id)}
                                className="text-teal-600 hover:text-teal-800 text-sm"
                              >
                                {showUploadedForms[project.id] ? 'Hide' : 'Show'} ({project.uploadedForms.length})
                              </button>
                            </div>
                            
                            {showUploadedForms[project.id] && (
                              <div className="space-y-2">
                                {project.uploadedForms.map((file, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-white p-2 rounded">
                                    <div>
                                      <p className="text-xs font-medium">{file.name}</p>
                                      <p className="text-xs text-gray-600">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB • {new Date(file.uploadDate).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => downloadUploadedForm(file)}
                                      className="bg-teal-600 text-white px-2 py-1 rounded text-xs hover:bg-teal-700"
                                    >
                                      Download
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Customer Revision Documents */}
                        {project.revisionDocuments && project.revisionDocuments.length > 0 && (
                          <div className="bg-pink-50 p-4 rounded">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="font-semibold text-sm">Customer Revision Documents</h4>
                              <button
                                onClick={() => toggleRevisionDocs(project.id)}
                                className="text-pink-600 hover:text-pink-800 text-sm"
                              >
                                {showRevisionDocs[project.id] ? 'Hide' : 'Show'} ({project.revisionDocuments.length})
                              </button>
                            </div>
                            
                            {showRevisionDocs[project.id] && (
                              <div className="space-y-3">
                                {project.revisionDocuments.map((revision, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded border border-pink-200">
                                    <div className="mb-2">
                                      <span className="font-medium text-sm">Revision {revision.revisionNumber}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        {revision.uploadDate ? new Date(revision.uploadDate).toLocaleDateString() : ''}
                                      </span>
                                    </div>
                                    {revision.requirements && (
                                      <p className="text-xs text-gray-700 mb-2 bg-gray-50 p-2 rounded">
                                        <strong>Requirements:</strong> {revision.requirements}
                                      </p>
                                    )}
                                    {revision.files && revision.files.length > 0 && (
                                      <div className="space-y-1">
                                        <p className="text-xs font-medium text-gray-600">Attached Files:</p>
                                        {revision.files.map((file, fileIdx) => (
                                          <div key={fileIdx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                            <div>
                                              <p className="text-xs font-medium">{file.name}</p>
                                              <p className="text-xs text-gray-500">
                                                {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : ''}
                                              </p>
                                            </div>
                                            {file.url && (
                                              <a
                                                href={file.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="bg-pink-600 text-white px-2 py-1 rounded text-xs hover:bg-pink-700"
                                              >
                                                Download
                                              </a>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Communications */}
                        {project.communications && project.communications.length > 0 && (
                          <div className="bg-blue-50 p-4 rounded">
                            <h4 className="font-semibold text-sm mb-2">Recent Communications</h4>
                            <div className="max-h-48 overflow-y-auto space-y-2">
                              {project.communications.slice(-5).map((comm, idx) => (
                                <div key={idx} className={`p-2 rounded text-xs ${
                                  comm.type === 'client' ? 'bg-blue-100' : 
                                  comm.type === 'architect' ? 'bg-green-100' : 'bg-gray-100'
                                }`}>
                                  <div className="flex justify-between mb-1">
                                    <span className="font-medium">{comm.sender}</span>
                                    <span className="text-gray-500">{comm.timestamp}</span>
                                  </div>
                                  <p>{comm.message}</p>
                                  {comm.attachment && (
                                    <div className="mt-1">
                                      <a 
                                        href={comm.attachment.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 text-xs underline"
                                      >
                                        📎 {comm.attachment.name}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Deliverables */}
                        {project.deliverables && project.deliverables.length > 0 && (
                          <div className="bg-purple-50 p-4 rounded">
                            <h4 className="font-semibold text-sm mb-2">Deliverables</h4>
                            <div className="space-y-2">
                              {project.deliverables.map((deliverable, idx) => (
                                <div key={idx} className="bg-white p-2 rounded">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h5 className="font-medium text-xs">{deliverable.title}</h5>
                                      <p className="text-xs text-gray-600">{deliverable.description}</p>
                                      <p className="text-xs text-gray-500">
                                        Uploaded: {deliverable.uploadDate}
                                        {deliverable.isRevision && ' (Revision)'}
                                      </p>
                                      {deliverable.files && deliverable.files.length > 0 && (
                                        <div className="mt-1">
                                          <p className="text-xs text-gray-500">{deliverable.files.length} file(s)</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {sortedProjects.length === 0 && (
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-12 text-center">
                  <div className="text-gray-400 text-5xl mb-4">📋</div>
                  <p className="text-gray-600">No projects found matching your filters</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* All the modals */}
      {showPaymentModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Request Payment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <p className="text-sm text-gray-600">{selectedProject.name} - {selectedProject.client}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount ($)</label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  placeholder="150.00"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  placeholder="Progress payment for Stage 2"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handlePaymentRequest}
                className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700"
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}

      {showMessageModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Send Message</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <p className="text-sm text-gray-600">{selectedProject.name} - {selectedProject.client}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Message Type</label>
                <select
                  value={messageForm.type}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                >
                  <option value="architect">From planAIdesign</option>
                  <option value="system">System Message</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  value={messageForm.message}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  rows={4}
                  placeholder="Enter your message to the client..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Attachment (Optional)</label>
                <input
                  type="file"
                  onChange={(e) => setMessageForm(prev => ({ ...prev, attachment: e.target.files[0] }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                />
                {uploadProgress.attachment !== undefined && (
                  <div className="mt-2">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress.attachment}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowMessageModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                className="flex-1 bg-purple-600 text-white py-2 rounded hover:bg-purple-700"
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocumentModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Upload Documents</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <p className="text-sm text-gray-600">{selectedProject.name} - Stage {selectedProject.stage} - {selectedProject.client}</p>
              </div>
              
              {selectedProject.stage === 2 && selectedProject.status === 'quote-in-progress' && (
                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    This project requires a custom quote. Click "Upload Documents" to proceed with quote upload.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={documentForm.description}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  placeholder={
                    selectedProject.stage === 1 ? "Feasibility report description" : 
                    selectedProject.stage === 2 ? "Concept design description" :
                    `${documentTypes[`stage${selectedProject.stage}`]?.[selectedDocumentType] || selectedDocumentType} description`
                  }
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Files</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.dwg,.jpg,.jpeg,.png"
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, files: Array.from(e.target.files) }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                />
                {documentForm.files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {documentForm.files.map((file, idx) => (
                      <div key={idx} className="text-xs text-gray-600 flex justify-between items-center p-1 bg-gray-50 rounded">
                        <span>{file.name} - {(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentForm(prev => ({
                              ...prev,
                              files: prev.files.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="text-red-600 hover:text-red-800 ml-2 px-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Supports: PDF, DOC, DOCX, DWG, JPG, PNG (Max 10MB per file)
                </p>
              </div>
              
              {Object.keys(uploadProgress).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(uploadProgress).map(([fileName, progress]) => (
                    <div key={fileName}>
                      <div className="flex justify-between text-xs">
                        <span>{fileName}</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowDocumentModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDocumentUpload}
                className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
              >
                {selectedProject.stage === 2 && selectedProject.status === 'quote-in-progress' ? 
                  'Proceed to Quote Upload' : 'Upload Documents'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuoteModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">
              {selectedProject.stage >= 3 ? `Upload Stage ${selectedProject.stage} Quote` : 'Upload Stage 2 Custom Quote'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <p className="text-sm text-gray-600">{selectedProject.name} - {selectedProject.client}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Quote Amount ($)</label>
                <input
                  type="number"
                  value={quoteForm.amount}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  placeholder="750.00"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Quote Description</label>
                <textarea
                  value={quoteForm.description}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  rows={3}
                  placeholder={
                    selectedProject.stage >= 3 ? 
                    `Custom quote for Stage ${selectedProject.stage} services...` :
                    "Custom quote for complex site/design requirements. Additional fees apply due to..."
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Quote Document (Optional)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, quoteFile: e.target.files[0] }))}
                  className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                />
                {uploadProgress.quote !== undefined && (
                  <div className="mt-2">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress.quote}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Upload detailed quote PDF (optional)
                </p>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowQuoteModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleQuoteUpload}
                className="flex-1 bg-yellow-600 text-white py-2 rounded hover:bg-yellow-700"
              >
                Upload Quote
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage 1 Payment Request Modal */}
      {showInvoiceModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-lg w-full">
            {/* Different header based on whether payment request is already sent */}
            {selectedProject.status === 'invoice-sent' || selectedProject.paymentStatus === 'invoice-sent' ||
             selectedProject.status === 'payment-due' || selectedProject.paymentStatus === 'payment-due' || invoicePaymentLink ? (
              <>
                <h3 className="text-lg font-bold mb-4 text-green-600">✅ Payment Requested</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Project</label>
                    <p className="text-sm text-gray-600">{selectedProject.name} - {selectedProject.client}</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded border border-green-200">
                    <p className="text-sm text-green-800 mb-2">
                      <strong>Payment request has been sent to the customer.</strong>
                    </p>
                    <p className="text-xs text-green-700">
                      Customer can pay via the link, or their card will be automatically charged in 5 days.
                    </p>
                  </div>
                  
                  {selectedProject.invoice && (
                    <div className="bg-blue-50 p-3 rounded border border-blue-200">
                      <p className="text-sm text-blue-800 font-medium">📄 Invoice Attached</p>
                      <p className="text-xs text-blue-700">{selectedProject.invoice.name}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Payment Link (for manual sharing)</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={invoicePaymentLink || selectedProject.paymentLink || ''}
                        readOnly
                        className="flex-1 p-2 border rounded bg-gray-50 text-xs"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(invoicePaymentLink || selectedProject.paymentLink || '');
                          showNotification('Copied', 'Payment link copied to clipboard', 'success');
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                    <p className="text-xs text-yellow-800">
                      <strong>Auto-charge:</strong> If payment is not received within 5 days, the captured card will be automatically charged.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setInvoicePaymentLink('');
                      setInvoiceFile(null);
                    }}
                    className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
                  >
                    Close
                  </button>
                  <a
                    href={invoicePaymentLink || selectedProject.paymentLink || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 text-center"
                  >
                    Open Payment Link
                  </a>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold mb-4">📧 Request Payment</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Project</label>
                    <p className="text-sm text-gray-600">{selectedProject.name} - {selectedProject.client}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Amount: ${getStage1Price(selectedProject.type, selectedProject.townhouseUnits) || 199}
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded border border-blue-200">
                    <p className="text-sm text-blue-800">
                      This will send a payment request to the customer. They can pay online or their captured card will be auto-charged in 5 days.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Attach Invoice PDF (Optional)</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setInvoiceFile(e.target.files[0])}
                      className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                    />
                    {invoiceFile && (
                      <div className="mt-2 flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-xs text-gray-600">{invoiceFile.name}</span>
                        <button
                          onClick={() => setInvoiceFile(null)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Upload your tax invoice PDF. Customer will be able to download it.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setInvoiceFile(null);
                    }}
                    className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
                    disabled={invoiceUploading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStage1Invoice(selectedProject, invoiceFile)}
                    disabled={invoiceUploading}
                    className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {invoiceUploading ? 'Sending...' : '📧 Request Payment'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Receipt Upload Modal */}
      {showReceiptModal && receiptProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-lg w-full">
            <h3 className="text-lg font-bold mb-4">📄 Upload Payment Receipt</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <p className="text-sm text-gray-600">{receiptProject.name} - {receiptProject.client}</p>
              </div>

              {receiptProject.receipt ? (
                <div className="bg-green-50 p-4 rounded border border-green-200">
                  <p className="text-sm text-green-800 font-medium">✅ Receipt Already Uploaded</p>
                  <p className="text-xs text-green-700 mt-1">{receiptProject.receipt.name}</p>
                  <a
                    href={receiptProject.receipt.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-xs mt-2 inline-block"
                  >
                    View Receipt →
                  </a>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2">Receipt PDF</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="w-full p-2 border rounded focus:border-blue-500 focus:outline-none"
                  />
                  {receiptFile && (
                    <div className="mt-2 flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-xs text-gray-600">{receiptFile.name}</span>
                      <button
                        onClick={() => setReceiptFile(null)}
                        className="text-red-600 hover:text-red-800 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Upload the payment receipt for the customer to download.
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setShowReceiptModal(false);
                  setReceiptFile(null);
                  setReceiptProject(null);
                }}
                className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600"
              >
                {receiptProject.receipt ? 'Close' : 'Cancel'}
              </button>
              {!receiptProject.receipt && (
                <button
                  onClick={handleReceiptUpload}
                  disabled={receiptUploading || !receiptFile}
                  className="flex-1 bg-teal-600 text-white py-2 rounded hover:bg-teal-700 disabled:bg-gray-400"
                >
                  {receiptUploading ? 'Uploading...' : '📄 Upload Receipt'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <DocumentTypeSelectorModal
        show={showDocumentTypeSelector}
        onClose={() => {
          setShowDocumentTypeSelector(false);
          setDocumentSelectorProject(null);
          setDocumentSelectorAction('');
        }}
        project={documentSelectorProject}
        onSelect={handleDocumentTypeSelected}
        action={documentSelectorAction}
      />
    </div>
  );
};

export default AdminPanel;